import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useStore, localMsgID } from './lib/store.js';
import { startWS, subscribeChannel, markRead, onStatus } from './lib/wsbridge.js';
import { mergeStreamingDeltas, streamSeq } from './lib/events.js';
import { useThreadArchivistEvents } from './lib/useThreadArchivistEvents.js';
import { Avatar } from './components/Avatar.jsx';
import { V2Row } from './components/V2Row.jsx';
import { V2Empty } from './components/V2Empty.jsx';
import { V2Breadcrumb } from './components/V2Breadcrumb.jsx';
import { V2BranchHeader } from './components/V2BranchHeader.jsx';
import { V2RightPane } from './components/V2RightPane.jsx';
import { ArchivistBeat } from './components/ArchivistBeat.jsx';

function ConnectionChip({ status }) {
  const label = status === 'open' ? 'live' : status === 'connecting' ? 'connecting…' : 'offline';
  return <span className={`chatv2-connection-chip chatv2-conn-${status}`} data-testid="chatv2-conn-chip">{label}</span>;
}

export default function ChatV2Modal({ chatTo, onClose }) {
  const channelID = `dm:${chatTo}`;

  const [events, setEvents] = useState([]);
  const [currentThread, setCurrentThread] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [stagingNewThread, setStagingNewThread] = useState(false);
  const [wsStatus, setWsStatus] = useState('connecting');
  const [tab, setTab] = useState('summary');

  const activeBranchId = useStore(
    s => s.branchesByChannel[channelID]?.currentByThread?.get(currentThread) ?? null
  );
  const enqueueChatSend = useStore(s => s.enqueueChatSend);
  const me = useStore(s => s.me);
  const principalURN = me?.urn || null;

  // JOBE-0143.3: live archivist events from SSE stream.
  const archivistEvs = useThreadArchivistEvents(currentThread);

  const streamRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const stop = startWS();
    const offStatus = onStatus(setWsStatus);
    return () => { stop(); offStatus(); };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!chatTo) return;

    let cancelled = false;
    let threadURN = null;

    async function hydrate() {
      const threadsRes = await window.fetch(`/api/channels/${channelID}/threads?limit=1`);
      const threadsData = await threadsRes.json();

      if (cancelled) return;

      const threadList = Array.isArray(threadsData) ? threadsData : (threadsData.threads || []);
      if (threadList.length > 0) {
        threadURN = threadList[0].thread_urn;
        setCurrentThread(threadURN);

        setHistoryLoading(true);
        const eventsRes = await window.fetch(`/api/threads/${threadURN}/events?limit=200`);
        const eventsData = await eventsRes.json();

        if (cancelled) return;

        const loaded = eventsData.events || [];
        setEvents(loaded);
        setHistoryLoading(false);

        if (loaded.length > 0) {
          const lastSeq = streamSeq(loaded[loaded.length - 1]);
          markRead(channelID, threadURN, lastSeq);
        }
      } else {
        setStagingNewThread(true);
        // No markRead here — no thread exists yet; the handler below guards with `if (threadURN)`.
      }

      if (cancelled) return;
      const unsub = subscribeChannel(channelID, (ev) => {
        setEvents(prev => {
          if (ev.event_id && prev.some(e => e.event_id === ev.event_id)) return prev;
          return [...prev, ev];
        });
        if (threadURN) {
          markRead(channelID, threadURN, streamSeq(ev));
        }
      });

      return unsub;
    }

    let cleanupSub = null;
    hydrate().then(unsub => {
      if (unsub) cleanupSub = unsub;
    });

    return () => {
      cancelled = true;
      if (cleanupSub) cleanupSub();
    };
  }, [chatTo, channelID]);

  const threadTitle = useMemo(() => {
    for (const ev of events) {
      const body = ev?.body || ev?.payload || {};
      const role = typeof body.role === 'string' ? body.role
        : typeof body.author === 'string' ? body.author
        : typeof body.from === 'string' ? body.from
        : '';
      const content = typeof body.content === 'string' ? body.content
        : typeof body.text === 'string' ? body.text
        : '';
      if ((role === 'user' || role === 'human') && content) {
        return content.slice(0, 60) + (content.length > 60 ? '…' : '');
      }
    }
    return typeof chatTo === 'string' ? chatTo : '';
  }, [events, chatTo]);

  const renderedEvents = useMemo(() => {
    const merged = mergeStreamingDeltas(events);
    if (!activeBranchId) return merged;
    return merged.filter(ev => ev.localBranchID == null || ev.localBranchID === activeBranchId);
  }, [events, activeBranchId]);

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [renderedEvents]);

  const handleApprovalResolved = useCallback((requestID, decision) => {}, []);

  const handleScrollTo = useCallback((msgId) => {
    const el = streamRef.current?.querySelector(`[data-msg-id="${msgId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  // TODO(0137+0139): mount V2RightPane and wire onScrollTo={handleScrollTo}

  function handleSend(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!draft.trim()) return;
    const localID = localMsgID();
    const optimistic = {
      event_id: localID,
      ts: new Date().toISOString(),
      body: { content: draft.trim(), role: 'user' },
      event_type: 'message.created',
      ...(activeBranchId ? { localBranchID: activeBranchId } : {}),
    };
    setEvents(prev => [...prev, optimistic]);
    setDraft('');
    enqueueChatSend({
      channelID,
      threadURN: stagingNewThread ? null : currentThread,
      agent: chatTo,
      message: draft.trim(),
      ...(stagingNewThread ? { stagingConvID: null, ignoreHistory: true } : {}),
    });
    // TODO(0136): clear stagingNewThread on reconcile
  }

  function handleComposerKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="chatv2-modal" role="dialog" aria-modal="true">
      <div className="chatv2-backdrop" onClick={onClose} />
      <div className="chatv2-frame">
        <div className="chatv2-topbar" data-testid="chatv2-topbar">
          <Avatar name={chatTo} size={28} />
          <span className="chatv2-agent-name">{chatTo}</span>
          {currentThread && (
            <V2Breadcrumb channelID={channelID} threadURN={currentThread} />
          )}
          {currentThread && (
            <span className="chatv2-thread-kicker">
              <span className="chatv2-thread-kicker-label">Thread</span>
              <span className="chatv2-thread-sep">·</span>
              <span className="chatv2-thread-title">{threadTitle}</span>
            </span>
          )}
          <ArchivistBeat events={archivistEvs} />
          <ConnectionChip status={wsStatus} />
          <button
            type="button"
            className="chatv2-close"
            onClick={onClose}
            data-testid="chatv2-close"
          >✕</button>
        </div>
        <div className="chatv2-left" data-testid="chatv2-left">
          {activeBranchId !== null && (
            <V2BranchHeader channelID={channelID} threadURN={currentThread} />
          )}
          <div className="chatv2-stream" data-testid="chatv2-stream" ref={streamRef}>
            {historyLoading && <div className="chatv2-loading">Loading…</div>}
            {!historyLoading && renderedEvents.length === 0 && <V2Empty />}
            {renderedEvents.map((ev, i) => (
              <V2Row
                key={ev.event_id || i}
                ev={ev}
                events={renderedEvents}
                onResolved={handleApprovalResolved}
                threadURN={currentThread}
                channelID={channelID}
                currentBranchId={activeBranchId}
              />
            ))}
            <div ref={bottomRef} />
          </div>
          <form className="chatv2-composer" onSubmit={handleSend} data-testid="chatv2-composer">
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleComposerKey}
              placeholder={stagingNewThread ? 'Start a new conversation…' : 'Message…'}
              data-testid="chatv2-composer-input"
            />
            <button type="submit" disabled={!draft.trim()} data-testid="chatv2-send-btn">Send</button>
          </form>
        </div>
        <V2RightPane
          tab={tab}
          onTabChange={setTab}
          events={renderedEvents}
          threadURN={currentThread}
          principalURN={principalURN}
          onScrollTo={handleScrollTo}
          currentBranch={activeBranchId ? { threadURN: currentThread, branchId: activeBranchId } : null}
        />
      </div>
    </div>
  );
}
